import qs from 'query-string'
import { z } from 'zod'
import Axios from 'axios'

import { BASE_URL, USER_SERVICE_BASE_URL } from '../config'

import customFetch from './../../function/customFetch'
import axios from './../../function/axiosInstance'
import extract from './../../function/extract'

const url = `${BASE_URL}/users`
const companyUrl = `${BASE_URL}/companies`

interface LastCheckoutParams {
  id: string
  page?: number
  limit?: number
  sort: string
}

const recordSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  user_id: z.string()
})
const checkInSchema = z.object({ records: z.array(recordSchema) })

type checkInResponse = z.infer<typeof checkInSchema>

export const fetchLastCheckInDate = async ({
  id,
  page = 0,
  limit = 1,
  sort
}: LastCheckoutParams): Promise<ApiResponseV2<checkInResponse>> => {
  const URL = `${USER_SERVICE_BASE_URL}/${id}/check-ins`
  try {
    const request = await axios.get(URL, { params: { page, limit, sort } })
    checkInSchema.parse(request.data)
    return extract.data<checkInResponse>(request)
  } catch (error) {
    return extract.error(error)
  }
}

export const fetchUser = async ({
  id
}: {
  id: string | null | undefined
}): Promise<{
  status: string
  data: User | null
  error: string | undefined
}> => {
  try {
    if (!id) throw new Error('Please login again')

    const response = await axios.get(`/users/${id}`)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const fetchUserDetails = async ({
  id
}: {
  id: string | null | undefined
}): Promise<GetUserDetails> => {
  try {
    if (!id) throw new Error('Please login again')

    const response = await axios.get(`/users/${id}/details`)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const fetchRecommendedUsers = async (
  assignmentIDs: string[],
  projectId?: string,
  fetchMargin?: boolean
): Promise<ApiResponse> => {
  try {
    if (!Array.isArray(assignmentIDs))
      throw new Error('assignmentIds must be an array')

    if (assignmentIDs.length === 1) {
      const [assignmentID] = assignmentIDs
      const response = await axios.get(
        `/recommendations/v3?assignment_id=${assignmentID}&fetch_margin=${fetchMargin}`
      )
      return extract.data(response)
    }

    if (projectId === '')
      throw new Error('projectID must be present for multiple assignments')

    const params = qs.stringify(
      {
        assignment_ids: assignmentIDs,
        project_id: projectId
      },
      { arrayFormat: 'comma' }
    )
    const response = await axios.get(`recommendations/bulk?${params}`)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const editDetails = async ({
  id,
  body
}: {
  id: string | undefined | null
  body: Record<string, unknown>
}): Promise<{
  status: string
  description: string
}> => {
  try {
    if (!id) throw new Error('Please login again')

    const response = await axios.put(`${url}/${id}/details`, body)
    return {
      status: 'ok',
      description: response.data.description
    }
  } catch (error) {
    return {
      status: 'error',
      description: extract.error(error).error as string
    }
  }
}

export const findLocationByZipCode = async ({
  zipCode
}: {
  zipCode: string
}): Promise<ApiResponse> => {
  try {
    const response = await Axios(
      `https://api.postalpincode.in/pincode/${zipCode}`
    )
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const editUserRole = async ({
  id,
  role
}: {
  id: string | undefined | null
  role: UserRole
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    if (!id) throw new Error('Please login again')
    const response = await customFetch(`${url}/${id}/role`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ role })
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.description,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const fetchList = async ({
  query_params = {},
  page = 0,
  page_size = 10,
  fetchAll = false
}: {
  query_params?: Partial<UserQueryParams>
  page?: number
  page_size?: number
  fetchAll?: boolean
}): Promise<GetUserList> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }

  // adding query params
  const reqQueryParams = new URLSearchParams()
  if (!fetchAll) {
    reqQueryParams.append('current_page', page.toString())
    reqQueryParams.append('page_size', page_size.toString())
  }

  Object.keys(query_params).forEach(key => {
    const queryParam = query_params[key]
    // adding query param only if it has valid value (undefined or null or empty string or [])
    if (queryParam?.toString()) {
      reqQueryParams.set(key, queryParam.toString())
    }
  })
  try {
    const response = await customFetch(`${url}?${reqQueryParams.toString()}`, {
      headers
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: {
          records: result.records,
          total_records: result.total_records
        },
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: {
        records: [],
        total_records: 0
      },
      error: error.message
    }
  }
}

export const fetchOrganizationsList = async ({
  query_params = {},
  page = 0,
  page_size = 10,
  fetchAll = false
}: {
  query_params?: Partial<UserQueryParams>
  page?: number
  page_size?: number
  fetchAll?: boolean
}): Promise<GetOrganizationList> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }

  // adding query params
  const reqQueryParams = new URLSearchParams()
  if (!fetchAll) {
    reqQueryParams.append('current_page', page.toString())
    reqQueryParams.append('page_size', page_size.toString())
  }
  Object.keys(query_params).forEach(key => {
    const queryParam = query_params[key]
    // adding query param only if it has valid value (undefined or null or empty string or [])
    if (queryParam?.toString()) {
      reqQueryParams.set(key, queryParam.toString())
    }
  })
  try {
    const response = await customFetch(
      `${companyUrl}?${reqQueryParams.toString()}`,
      {
        headers
      }
    )
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: {
          records: result.records,
          total_records: result.total_records
        },
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: {
        records: [],
        total_records: 0
      },
      error: error.message
    }
  }
}

export const onboardUsers = async (
  user_list: string[],
  operation: OnboardingStatus,
  message?: string
) => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/onboarding-status`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        ids: user_list,
        status: operation,
        ...(message && { onboarding_message: message })
      })
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.description,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const companyDetails = async (
  body: Record<string, unknown>
): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${companyUrl}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const getCompanyPrefillData = async (
  id: string
): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await axios
      .get(`${companyUrl}/${id}/prefill-biodata`, { headers })
      .then(_response => {
        _response.data.data = _response.data.prefill_biodata
        return _response
      })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const editCompanyDetails = async ({
  id,
  body
}: {
  id: string | undefined | null
  body: Record<string, unknown>
}): Promise<{
  status: string
  description: string
}> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    if (!id) throw new Error('Please login again')
    const response = await customFetch(`${companyUrl}/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        description: result.description
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      description: error.message
    }
  }
}

export const fetchTeamMemberList = async ({
  id
}: {
  id: string | null | undefined
}): Promise<GetTeamMembers> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }
  try {
    if (!id) throw new Error('Please login again')

    const response = await customFetch(`${companyUrl}/${id}/team-members`, {
      headers
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.data,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: [],
      error: error.message
    }
  }
}

export const addTeamMembers = async ({
  body,
  id
}: {
  body: { email: string; role: TeamMemberRole }
  id: string
}): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${USER_SERVICE_BASE_URL}/companies/${id}/team-members`,
      [body],
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      }
    )

    if (response.status === 201) {
      return {
        status: 'ok',
        data: response.data,
        error: undefined
      }
    }
    throw new Error(response?.data?.message)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.response?.data?.message
    }
  }
}

export const createContract = async ({
  id
}: {
  id: string | null | undefined
}): Promise<GetContracts> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }
  try {
    if (!id) throw new Error('Please login again')

    const response = await customFetch(`${url}/${id}/contracts`, {
      headers,
      method: 'POST',
      body: JSON.stringify({})
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.data,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const fetchContracts = async ({
  id
}: {
  id: string | null | undefined
}): Promise<GetContracts> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }
  try {
    if (!id) throw new Error('Please login again')

    const response = await customFetch(`${url}/${id}/contracts`, {
      headers
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.data,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const selectManager = async ({
  body,
  id
}: {
  body: {
    user_list: { user_id: string; remarks: string; user_tag_type: string }[]
  }

  id: string
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${companyUrl}/${id}/managers`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.data,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const editClientStatus = async ({
  id,
  body
}: {
  id: string
  body: { status: ClientStatus }
}): Promise<{
  status: string
  description: string
}> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    if (!id) throw new Error('Please login again')
    const response = await customFetch(`${companyUrl}/${id}/status`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        description: result.description
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      description: error.message
    }
  }
}

export const editClientAccountType = async ({
  id,
  body
}: {
  id: string
  body: { type: ClientAccountType; billing_currency_id?: number }
}): Promise<{
  status: string
  description: string
}> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    if (!id) throw new Error('Please login again')
    const response = await customFetch(`${companyUrl}/${id}/type`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        description: result.description
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      description: error.message
    }
  }
}

export const updateGenreVerticals = async ({
  body
}: {
  body: Record<string, unknown>
}): Promise<{
  status: string
  description: string
}> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/requests`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        description: result.description
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      description: error.message
    }
  }
}

export const fetchChangeHistory = async ({
  query_params = {},
  page = 0,
  page_size = 10,
  id
}: {
  query_params?: Partial<ChangeHistoryQueryParams>
  page: number
  page_size: number
  id: string | null
}): Promise<GetChangesHistory> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }
  // adding query params
  const reqQueryParams = new URLSearchParams()
  reqQueryParams.append('page', page.toString())
  reqQueryParams.append('page_size', page_size.toString())

  Object.keys(query_params).forEach(key => {
    const queryParam = query_params[key]
    // adding query param only if it has valid value (undefined or null or empty string or [])
    if (queryParam?.toString()) {
      reqQueryParams.append(key, queryParam.toString())
    }
  })

  try {
    const response = await customFetch(
      `${url}/${id}/requests?${reqQueryParams.toString()}`,
      {
        headers
      }
    )
    const result = await response.json()
    if (response.ok) {
      // next page number needed for useInfiniteQuery hook
      const next_page =
        result.total_records - page_size * (page + 1) > 0 ? page + 1 : null
      return {
        status: 'ok',
        data: {
          records: result.records,
          total_records: result.total_records,
          next_page
        },
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: {
        records: [],
        total_records: 0,
        next_page: null
      },
      error: error.message
    }
  }
}

export const fetchRequestList = async ({
  query_params = {},
  page = 0,
  page_size = 100
}: {
  query_params?: Partial<RequestQueryParams>
  page?: number
  page_size?: number | undefined
}): Promise<GetCreatorRequestList> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }
  // adding query params
  const reqQueryParams = new URLSearchParams()
  reqQueryParams.append('page', page.toString())
  reqQueryParams.append('page_size', page_size.toString())
  Object.keys(query_params).forEach(key => {
    const queryParam = query_params[key]
    // adding query param only if it has valid value (undefined or null or empty string or [])
    if (queryParam?.toString()) {
      reqQueryParams.set(key, queryParam.toString())
    }
  })
  try {
    const response = await customFetch(
      `${url}/requests?${reqQueryParams.toString()}`,
      {
        headers
      }
    )
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: {
          records: result.records,
          total_records: result.total_records
        },
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: {
        records: [],
        total_records: 0
      },
      error: error.message
    }
  }
}

export const fetchUserRequestList = async ({
  query_params = {},
  page = 0,
  page_size = 100,
  id
}: {
  query_params?: Partial<RequestQueryParams>
  page?: number
  page_size?: number | undefined
  id: string
}): Promise<GetCreatorRequestList> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }
  // adding query params
  const reqQueryParams = new URLSearchParams()
  reqQueryParams.append('page', page.toString())
  reqQueryParams.append('page_size', page_size.toString())
  Object.keys(query_params).forEach(key => {
    const queryParam = query_params[key]
    // adding query param only if it has valid value (undefined or null or empty string or [])
    if (queryParam?.toString()) {
      reqQueryParams.set(key, queryParam.toString())
    }
  })
  try {
    const response = await customFetch(
      `${url}/${id}/requests?${reqQueryParams.toString()}`,
      {
        headers
      }
    )
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: {
          records: result.records,
          total_records: result.total_records
        },
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: {
        records: [],
        total_records: 0
      },
      error: error.message
    }
  }
}

export const acceptRejectRequest = async ({
  body,
  id
}: {
  body: {
    status: 'approve' | 'reject'
    reason?: string | null
    proficiency_type_id?: number | null
  }
  id: string
}): Promise<{
  status: string
  description: string
}> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${url}/requests/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        description: result.description
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      description: error.message
    }
  }
}

export const fetchUserRequestMetrics = async (): Promise<GetRequestMetrics> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }

  try {
    const response = await customFetch(`${url}/requests/metrics`, {
      headers
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.data,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: {
        open_requests: 0,
        closed_requests: 0,
        total_requests: 0
      },
      error: error.message
    }
  }
}

export const updateStatusToContractSigned = async ({
  id,
  body
}: {
  id: string
  body: { stage: string }
}): Promise<{
  status: string
  description: string
}> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    if (!id) throw new Error('Please login again')
    const response = await customFetch(`${BASE_URL}/users/${id}/details`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        description: result.description
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      description: error.message
    }
  }
}

export const updateEmailPreferences = async ({
  companyId,
  body
}: {
  companyId: string
  body: EmailPreference
}): Promise<{
  status: 'ok' | 'error'
  data: EmailPreference | null
  error: string | undefined
}> => {
  try {
    const response = await axios.put(
      `/companies/${companyId}/preferences`,
      body
    )
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const fetchCompanyDetails = async (
  id: string,
  includeFilter = false
): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${companyUrl}/${id}?include_filters=${+includeFilter}`
    )
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

interface ReferBody {
  referee_email: string
  suggested_role: string
}

export const checkReferral = async ({
  body,
  id
}: {
  body: ReferBody
  id: string | undefined | null
}): Promise<ApiResponse> => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  }
  try {
    const response = await customFetch(`${BASE_URL}/refer`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result.points,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const deleteUser = async ({
  id
}: {
  id: string
}): Promise<ApiResponse> => {
  try {
    const response = await axios.delete(`${USER_SERVICE_BASE_URL}/${id}`)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getReviewAndRatingsList = async ({
  userId,
  query_params = {},
  page = 0,
  page_size = 7
}: {
  userId: string
  query_params?: Partial<UserQueryParams>
  page?: number
  page_size?: number
}): Promise<ApiResponse> => {
  const reqQueryParams = new URLSearchParams()
  reqQueryParams.append('current_page', page.toString())
  reqQueryParams.append('page_size', page_size.toString())
  Object.keys(query_params).forEach(key => {
    const queryParam = query_params[key]
    if (queryParam?.toString()) {
      reqQueryParams.set(key, queryParam.toString())
    }
  })
  try {
    const response = await axios.get(
      `/assignments?${reqQueryParams.toString()}&stage=approved&sort_by=allotted_date&sort_order=desc&client_feedback=true&user_id=${userId}`
    )
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getUserRedressalHistory = async ({
  id
}: {
  id: string
}): Promise<ApiResponse> => {
  try {
    const response = await axios.get(`/users/${id}/action`)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const markFavourite = async ({
  companyId,
  creatorId,
  assignmentId
}: {
  companyId: string
  creatorId: string
  assignmentId: string | undefined
}): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/companies/${companyId}/favourite`,
      {
        favourite_user_id: [creatorId],
        assignment_id: assignmentId
      }
    )
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const markUnfavourite = async ({
  companyId,
  creatorId,
  unfavourite_comment,
  unfavourite_reason_id
}: {
  companyId: string
  creatorId: string
  unfavourite_comment: string | undefined
  unfavourite_reason_id: string
}): Promise<ApiResponse> => {
  try {
    const response = await axios.delete(
      `/companies/${companyId}/favourite/${creatorId}?accept_user_id=true&unfavourite_comment=${unfavourite_comment}&unfavourite_reason_id=${unfavourite_reason_id}`
    )

    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}
